import { TextField } from '@openx/components-core/lib/TextField/TextField.tsx';
import {
  type IntersectOptionPayload,
  type InventoryAndContentState,
  type InventoryContentIdsTypeOption,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { AnyAction } from '@reduxjs/toolkit';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVENTORY_LIMITS } from '../../../shared/InventoryAndContent/constants.ts';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers.ts';
import {
  getTypeInventoryIDsPlaceholder,
  validateIfPublisherIdIsUsed,
  validateSingleRecordSubmissionInventoryIDs,
} from '../../../shared/InventoryAndContent/utils.ts';

type InventoryIdsProps = {
  dispatchInventory: (action: AnyAction) => void;
  inventoryContent: IntersectOptionPayload;
  selectedType: InventoryContentIdsTypeOption;
  targetingParams?: InventoryAndContentState;
};

const KeyboardInput = {
  ENTER: 'Enter',
} as const;

export const InventoryIDs = ({
  dispatchInventory,
  inventoryContent,
  selectedType,
  targetingParams,
}: InventoryIdsProps) => {
  const { t } = useTranslation();
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    setErrorText('');
  }, []);

  // App Bundle ID limit handling
  const { isAppBundleLimitReached, appBundleIdErrorText } = useMemo(() => {
    const appBundleIdLimit = INVENTORY_LIMITS[InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID];
    const appBundleIdErrorText = t('The limit has been reached ({limit}).', { limit: appBundleIdLimit });

    return {
      isAppBundleLimitReached: inventoryContent?.val?.length >= appBundleIdLimit,
      appBundleIdErrorText,
    };
  }, [inventoryContent, t]);

  const handleOnKeyUp = useCallback(
    (event: KeyboardEvent) => {
      const targetElement = event.target as HTMLInputElement;
      const { value } = targetElement;
      const inventoryContentOptionComparisonData = inventoryContent.val || [];

      if (event.key === KeyboardInput.ENTER) {
        const validationResult = validateSingleRecordSubmissionInventoryIDs(
          value,
          new Set([...inventoryContent.val]) || [],
          selectedType
        );

        if (validationResult?.error) {
          setErrorText(t(validationResult.errorMessage));
          return false;
        }

        if (selectedType === InventoryContentOption.PUBLISHER_ID) {
          const blockedPublisherIds = new Set(
            targetingParams?.[InventoryContentOption.PUBLISHER_INVENTORY_RULES].map(rule => rule.publisher_id)
          );

          const validatePublisherId = validateIfPublisherIdIsUsed(value, blockedPublisherIds);

          if (validatePublisherId?.error) {
            setErrorText(validatePublisherId.errorMessage);
            return false;
          }
        }

        dispatchInventory({
          payload: [...inventoryContentOptionComparisonData, value],
          type: setComparisonOpValue[selectedType],
        } as unknown as AnyAction);
        targetElement.value = '';
      }

      setErrorText('');
    },
    [dispatchInventory, inventoryContent, selectedType, targetingParams, t]
  );

  const labels = {
    [InventoryContentOption.PUBLISHER_ID]: t('Publisher ID'),
    [InventoryContentOption.INSTANCE_HASH]: t('Instance Hash'),
    [InventoryContentOption.ADUNIT]: t('Ad Unit ID'),
    [InventoryContentOption.SITE]: t('Site ID'),
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: t('App Bundle ID'),
  };

  return (
    <TextField
      name="inventory_ids"
      placeholder={isAppBundleLimitReached ? appBundleIdErrorText : getTypeInventoryIDsPlaceholder(t, selectedType)}
      fullWidth
      label={labels[selectedType]}
      data-test="inventory-and-content-input"
      autoComplete="off"
      onKeyUp={handleOnKeyUp}
      error={!!errorText}
      disabled={isAppBundleLimitReached}
      helperText={errorText}
    />
  );
};
